import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, SpinLoading } from "antd-mobile";
import { ScoreResultCard } from "./ScoreResultCard";
import type { CoachingSessionApi } from "./coachingSessionApi";
import type { ChatMessage } from "./types";

/**
 * 文本对练页(两条腿共用一份)。
 *
 * <p>此前 wecom-h5 的 CoachingChat 和 advisor-h5 的 CoachingChatPage 各写了一份**同一个状态机**:
 * 续练检测 → 恢复气泡 → 开练 → 逐轮发言 → finished → 轮询评分。判据很简单 ——「修一个 bug 要改
 * 几个地方」,当时是两个。语音页早就靠注入 API 收成了一份,文本页照抄。
 *
 * <p>drift 的实证:续练检测 member 侧早就有、tenant 侧长期是个桩(08-06 才补);「结束并评分」
 * advisor 侧有、member 侧没有(两边端点其实都在);字数计数只有 member 侧有。收成一份之后这些
 * 差异要么消失、要么变成一个显式的 prop。
 *
 * <p>**本组件不渲染页头**:member 侧是 NavBar、advisor 侧是 AdvisorPageHeader + 深色玻璃壳,
 * 那是两条腿真正不同的地方,留给各自的外壳。这里只管对话区 + 输入栏。
 */
export interface TextCoachingChatProps {
  scenarioId: number;
  /** 任务制练习的任务 id;空 = 自主练习(允许「再练一次」)。 */
  taskId?: number | null;
  api: CoachingSessionApi;
  /** 深色玻璃(advisor)/ 浅色(wecom)。只影响气泡与外框,不影响状态机。 */
  tone?: "light" | "dark";
  inputPlaceholder: string;
  sendLabel: string;
  backLabel: string;
  onBack: () => void;
  /** 起会话失败:member 侧 toast + 自动退回,advisor 侧整页错误态 —— 两边处置不同,交给外壳。 */
  onStartFailed?: (error: unknown) => void;
  /** 一局评估落定(用于刷新外部列表,如 my-home)。 */
  onEvaluated?: () => void;
}

const MAX_INPUT = 1500;

/** 练满两轮才亮「结束并评分」:防误触交空卷。 */
const MIN_TURNS_TO_FINISH = 2;

const TONE = {
  light: {
    shell: "flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-muted",
    mine: "bg-info text-info-foreground rounded-br-sm",
    theirs: "bg-white text-slate-900 border border-slate-200 rounded-bl-sm shadow-sm",
    bar: "shrink-0 bg-white border-t border-slate-200 px-3 py-2",
  },
  dark: {
    shell: "flex-1 min-h-0 overflow-y-auto px-4 py-2",
    mine: "av-glass rounded-br-sm",
    theirs: "av-glass rounded-bl-sm",
    bar: "shrink-0 px-4 py-2",
  },
} as const;

export function TextCoachingChat({
  scenarioId,
  taskId,
  api,
  tone = "light",
  inputPlaceholder,
  sendLabel,
  backLabel,
  onBack,
  onStartFailed,
  onEvaluated,
}: TextCoachingChatProps) {
  const { t } = useTranslation("coaching");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [starting, setStarting] = useState(true);
  const [sending, setSending] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [finished, setFinished] = useState(false);
  const [evalStatus, setEvalStatus] = useState<"evaluating" | "completed" | "failed">("evaluating");
  const [score, setScore] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [isLowConfidence, setIsLowConfidence] = useState(false);
  const [highlights, setHighlights] = useState<string[]>([]);
  const [improvements, setImprovements] = useState<string[]>([]);
  /** 自主练习「再练一次」:自增即重跑 init(任务制不给这个入口)。 */
  const [retryKey, setRetryKey] = useState(0);
  const bottomRef = useRef<HTMLDivElement>(null);

  // 回调存 ref:放进 effect deps 会让父组件每次 re-render 都重新拉会话。
  const onStartFailedRef = useRef(onStartFailed);
  onStartFailedRef.current = onStartFailed;
  const onEvaluatedRef = useRef(onEvaluated);
  onEvaluatedRef.current = onEvaluated;
  const apiRef = useRef(api);
  apiRef.current = api;

  const myTurns = messages.filter((m) => m.role === "user").length;
  const canFinishEarly = Boolean(api.finish) && !finished && myTurns >= MIN_TURNS_TO_FINISH;

  useEffect(() => {
    let cancelled = false;

    /**
     * ★重练(retryKey>0)必须跳过续练检测:否则 6h 内的旧会话会被当成「在途」续上,
     * 用户点了「再练一次」却回到上一局的对话。
     */
    async function resume(): Promise<boolean> {
      if (retryKey !== 0 || !apiRef.current.sessionActive) return false;
      try {
        const active = await apiRef.current.sessionActive(scenarioId, taskId);
        if (cancelled || !active?.sessionId) return false;
        setSessionId(active.sessionId);
        let restored: ChatMessage[] = [];
        try {
          const h = await apiRef.current.history?.(active.sessionId);
          // ai-service role 约定:'user' = 员工,其余 = 客户。
          restored = (h?.turns ?? []).map((turn, i) => ({
            id: String(i),
            role: turn.role === "user" ? ("user" as const) : ("assistant" as const),
            content: turn.content,
          }));
        } catch {
          // 历史拉不到不阻塞续聊,退到占位提示
        }
        if (cancelled) return true;
        setMessages(
          restored.length
            ? restored
            : [{ id: "0", role: "assistant", content: t("wecomChat.resumeHint") }]
        );
        return true;
      } catch {
        return false; // 续练检测失败一律走新建,不打扰用户
      }
    }

    async function init() {
      try {
        if (await resume()) return;
        const res = await apiRef.current.chatStart(scenarioId, taskId);
        if (cancelled) return;
        setSessionId(res.sessionId ?? null);
        setMessages([{ id: "0", role: "assistant", content: res.reply }]);
      } catch (e) {
        if (!cancelled) onStartFailedRef.current?.(e);
      } finally {
        if (!cancelled) setStarting(false);
      }
    }

    // 重练:上一局的成绩和气泡先清干净,不清就是新对话顶着旧分数
    setMessages([]);
    setFinished(false);
    setEvalStatus("evaluating");
    setScore(null);
    setFeedback(null);
    setIsLowConfidence(false);
    setHighlights([]);
    setImprovements([]);
    setStarting(true);
    void init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId, taskId, retryKey]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // finished 后轮询异步评估(最长 ~2min)。首拉立即 —— 已经评完的会话秒出结果。
  useEffect(() => {
    if (!finished || !sessionId || evalStatus !== "evaluating") return;
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      if (attempts >= 60) {
        // audit-0713 C1c:轮询用尽不再永转「正在评分」—— 转 failed 给可读文案,成绩后台仍会出
        setEvalStatus("failed");
        setFeedback(t("wecomChat.evalTimedOut"));
        return;
      }
      attempts += 1;
      try {
        const res = await apiRef.current.result(sessionId);
        if (cancelled) return;
        if (res.status === "completed") {
          setEvalStatus("completed");
          setScore(res.score);
          setFeedback(res.summary);
          setIsLowConfidence(res.isLowConfidence === 1);
          setHighlights(res.highlights ?? []);
          setImprovements(res.improvements ?? []);
          onEvaluatedRef.current?.();
          return;
        }
        if (res.status === "failed") {
          setEvalStatus("failed");
          setFeedback(res.error ?? t("wecomChat.evalFailed"));
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 3000); // 网络抖动重试,不打扰用户
      }
    };

    void poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished, sessionId, evalStatus]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || sending || finished || !sessionId) return;
    setInput("");
    setSending(true);
    const mine: ChatMessage = { id: `${Date.now()}`, role: "user", content: text };
    setMessages((prev) => [...prev, mine]);
    try {
      const res = await api.chat(sessionId, text, scenarioId);
      setMessages((prev) => [
        ...prev,
        { id: `${Date.now()}-ai`, role: "assistant", content: res.reply },
      ]);
      if (res.finished) {
        setFinished(true);
        setScore(res.score ?? null);
        setFeedback(res.feedback ?? null);
      }
    } catch {
      // 发失败就把那条气泡撤回来,别让用户以为发出去了
      setMessages((prev) => prev.filter((m) => m.id !== mine.id));
      setInput(text);
    } finally {
      setSending(false);
    }
  }, [api, finished, input, scenarioId, sending, sessionId]);

  /** 提前交卷:按当前对话直接评估,与 AI 自判 finished 同一条回写链路。 */
  const finishEarly = useCallback(async () => {
    if (!sessionId || finishing || !api.finish) return;
    setFinishing(true);
    try {
      await api.finish(sessionId, scenarioId);
      setFinished(true);
    } catch {
      // 交卷失败保持可继续聊的状态,不锁死
    } finally {
      setFinishing(false);
    }
  }, [api, finishing, scenarioId, sessionId]);

  const skin = TONE[tone];

  if (starting) {
    return (
      <div className="flex-1 flex items-center justify-center py-12">
        <SpinLoading color="primary" />
      </div>
    );
  }

  return (
    <>
      <div className={skin.shell}>
        {messages.map((msg, i) => (
          <div
            key={msg.id}
            className={`flex fx-enter mb-2.5 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            style={{ animationDelay: `${Math.min(i, 7) * 30}ms` }}
          >
            {msg.role === "assistant" && tone === "light" && (
              <div className="w-8 h-8 rounded-full bg-info flex items-center justify-center text-info-foreground text-xs shrink-0 mr-2 mt-0.5">
                {t("wecomChat.ai")}
              </div>
            )}
            <div
              className={`max-w-[76%] px-3 py-2 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${
                msg.role === "user" ? skin.mine : skin.theirs
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}

        {finished && evalStatus === "evaluating" && (
          <div className="flex flex-col items-center gap-2 py-4">
            <SpinLoading color="primary" />
            <p className="text-xs opacity-70">{t("wecomChat.evaluating")}</p>
          </div>
        )}

        {/* 结果卡与语音页 / 另一条腿共用同一个组件:档位、特效、两段反馈全同源 */}
        {finished && evalStatus !== "evaluating" && (
          <ScoreResultCard
            status={evalStatus}
            score={score}
            feedback={feedback}
            lowConfidence={isLowConfidence}
            highlights={highlights}
            improvements={improvements}
            tone={tone}
            onBack={onBack}
            backLabel={backLabel}
            onRetry={taskId ? undefined : () => setRetryKey((k) => k + 1)}
          />
        )}

        <div ref={bottomRef} />
      </div>

      {!finished && (
        <div
          className={skin.bar}
          style={{ paddingBottom: "max(8px, env(safe-area-inset-bottom))" }}
        >
          {/* 练满两轮给交卷出口:此前只有 AI 判 finished 一条路,学员想收只能干聊 */}
          {canFinishEarly && (
            <div className="flex justify-center mb-1.5">
              <button
                type="button"
                className="av-pill ghost"
                disabled={finishing}
                onClick={() => void finishEarly()}
              >
                {finishing ? t("wecomChat.finishing") : t("wecomChat.finishNow")}
              </button>
            </div>
          )}
          <div className="flex gap-2 items-end">
            <Input
              placeholder={inputPlaceholder}
              value={input}
              onChange={setInput}
              onEnterPress={() => void send()}
              maxLength={MAX_INPUT}
              className="flex-1"
              style={{ "--font-size": "14px" } as React.CSSProperties}
            />
            <Button
              color="primary"
              size="small"
              loading={sending}
              disabled={!input.trim()}
              onClick={() => void send()}
              className="shrink-0"
            >
              {sendLabel}
            </Button>
          </div>
          {/* 字数计数:backend @Size(max=2000),到 1500 就该提醒,别让人写完才被拒 */}
          <div className="mt-1 flex justify-end text-xs">
            <span
              className={
                input.length > 1300
                  ? "text-red-600"
                  : input.length > 1000
                    ? "text-amber-600"
                    : "opacity-50"
              }
            >
              {input.length} / {MAX_INPUT}
            </span>
          </div>
        </div>
      )}
    </>
  );
}
